import { useState } from 'react';
import type { ProductionBatch, ProductionEvent } from '@shared/domain';
import { useAuth } from '@/state/auth';
import { useAppendBatchEvent, useBatchEvents, useCreateBatch, useProduction } from '@/lib/query';
import { int, ts } from '@/lib/format';
import { Badge, Card, EmptyHint, MetricCard, SectionTitle } from '@/components/ui';
import { CostPanel } from '@/components/CostPanel';
import { PageBanner } from '@/components/layout/PageBanner';

const STATUS_LABEL: Record<string, string> = { growing: '养殖中', harvest_ready: '待捕捞', harvested: '已捕捞' };
const EVENT_LABEL: Record<string, string> = { stock: '放苗', feed: '投喂', mortality: '损耗', measurement: '测产', transfer: '转池', harvest: '捕捞' };

function EventHistory({ batchId }: { batchId: string }) {
  const { data: events } = useBatchEvents(batchId);
  return (
    <div className="space-y-1 rounded-lg bg-slate-50 p-3">
      {(events ?? []).map((e: ProductionEvent) => (
        <div key={e.id} className="flex justify-between text-[12px] text-slate-500">
          <span>{EVENT_LABEL[e.type] ?? e.type} · {e.qty}{e.note ? ` · ${e.note}` : ''}</span>
          <span className="tabular-nums">{ts(e.at, false)} · {e.by}</span>
        </div>
      ))}
      {(!events || events.length === 0) && <div className="text-[12px] text-slate-400">暂无事件</div>}
    </div>
  );
}

function BatchRow({ b, others, canRecord }: { b: ProductionBatch; others: ProductionBatch[]; canRecord: boolean }) {
  const [open, setOpen] = useState(false);
  const ev = useAppendBatchEvent();
  const act = (type: 'feed' | 'mortality' | 'measurement' | 'harvest', label: string, def: string) => {
    const q = window.prompt(`${label}（数量）`, def);
    if (q) ev.mutate({ id: b.id, body: { type, qty: Number(q) } });
  };
  const doTransfer = () => {
    if (others.length === 0) { window.alert('暂无其它批次可作为转池去向'); return; }
    const hint = others.map((o) => `${o.id}(${o.unit})`).join('、');
    const q = window.prompt('转池数量（尾）', '100');
    if (!q) return;
    const to = window.prompt(`转池去向批次 id：${hint}`, others[0].id);
    if (to) ev.mutate({ id: b.id, body: { type: 'transfer', qty: Number(q), toBatch: to.trim() } });
  };
  return (
    <>
      <tr className="border-b border-hairline/70 hover:bg-slate-50/50">
        <td className="td font-mono text-[13px]">{b.id}</td>
        <td className="td">{b.unit}</td>
        <td className="td">{b.species}</td>
        <td className="td text-right tabular-nums">{int(b.count)}</td>
        <td className="td text-right tabular-nums">{b.avgWeightKg.toFixed(2)}</td>
        <td className="td text-right tabular-nums">{int(b.biomassKg)}</td>
        <td className="td text-right tabular-nums">{b.dayAge}</td>
        <td className="td text-right tabular-nums">{b.survivalPct != null ? `${b.survivalPct}%` : '—'}</td>
        <td className="td text-right tabular-nums">{b.fcePct != null ? `${b.fcePct}%` : '—'}{b.fcr != null ? <span className="ml-1 text-[11px] text-slate-400">(FCR {b.fcr})</span> : null}</td>
        <td className="td text-right tabular-nums text-slate-500">{b.feedKgTotal != null ? int(b.feedKgTotal) : '—'}</td>
        <td className="td text-right tabular-nums text-slate-500">{b.mortalityTotal != null ? int(b.mortalityTotal) : '—'}</td>
        <td className="td text-right tabular-nums text-slate-500">{b.harvestTotalKg != null ? int(b.harvestTotalKg) : '—'}</td>
        <td className="td"><Badge className={b.status === 'harvest_ready' ? 'bg-warn-bg text-warn-fg border-warn-border' : b.status === 'harvested' ? 'bg-slate-100 text-slate-400 border-slate-200' : ''}>{STATUS_LABEL[b.status]}</Badge></td>
        <td className="td">
          <div className="flex flex-wrap items-center gap-2">
            {canRecord ? (
              <>
                <button onClick={() => act('feed', '投喂(kg)', '50')} className="text-brand-600 hover:underline">投喂</button>
                <button onClick={() => act('mortality', '损耗(尾)', '10')} className="text-brand-600 hover:underline">损耗</button>
                <button onClick={() => act('measurement', '测产(均重kg)', '0.5')} className="text-brand-600 hover:underline">测产</button>
                <button onClick={doTransfer} className="text-brand-600 hover:underline">转池</button>
                <button onClick={() => act('harvest', '捕捞(kg)', '100')} className="text-alarm-fg hover:underline">捕捞</button>
              </>
            ) : null}
            <button onClick={() => setOpen((o) => !o)} className="text-slate-500 hover:underline">{open ? '收起' : '历史'}</button>
          </div>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-hairline/70">
          <td colSpan={14} className="px-4 py-2"><EventHistory batchId={b.id} /></td>
        </tr>
      ) : null}
    </>
  );
}

export default function Production() {
  const { user } = useAuth();
  // Operators/supervisors/engineers/admin may record daily quantities; 放苗 (new
  // batch) stays supervisor+; cost parameters (price/operating cost) admin-only.
  const role = user?.role ?? '';
  const canRecord = ['operator', 'supervisor', 'engineer', 'admin'].includes(role);
  const canManage = ['supervisor', 'engineer', 'admin'].includes(role);
  const { data: summary } = useProduction();
  const batches = summary?.batches;
  const stats = summary?.stats;
  const create = useCreateBatch();
  const [form, setForm] = useState({ unit: '吊养#', species: '加州鲈', count: '10000', avgWeightKg: '0.3', stockedAt: new Date().toISOString().slice(0, 10) });

  const submit = () => {
    create.mutate({ unit: form.unit, species: form.species, count: Number(form.count), avgWeightKg: Number(form.avgWeightKg), stockedAt: form.stockedAt });
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">生产管理</h2>
        <p className="mt-1 text-[14px] text-slate-500">存塘批次 · 投喂/损耗/测产/捕捞事件（持久化 + 事件链）</p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-6">
        <MetricCard label="存塘批次" value={batches?.length ?? '—'} />
        <MetricCard label="现存生物量" value={int(stats?.standingBiomassKg ?? 0)} unit="kg" tone="brand" />
        <MetricCard label="今日投喂" value={summary?.feedTodayKg ?? 0} unit="kg" />
        <MetricCard label="今日损耗" value={summary?.mortalityToday ?? 0} unit="尾" tone="warn" />
        <MetricCard label="平均存活率" value={stats?.avgSurvivalPct != null ? `${stats.avgSurvivalPct}%` : '—'} />
        <MetricCard label="平均 FCE" value={stats?.avgFcePct != null ? `${stats.avgFcePct}%` : '—'} hint="增重/投喂" />
        <MetricCard label="饲料成本" value={stats?.pricingConfigured === false ? '未配置' : stats?.totalFeedCostYuan != null ? `¥${int(stats.totalFeedCostYuan)}` : '—'} hint="投喂量×当时单价" />
        <MetricCard label="单位成本" value={stats?.avgUnitCostYuanPerKg != null ? `¥${stats.avgUnitCostYuanPerKg}/kg` : '—'} tone="brand" hint="元/kg 产出鱼（仅饲料）" />
        <MetricCard label="完全成本" value={stats?.totalCostYuan != null ? `¥${int(stats.totalCostYuan)}` : '—'} hint="饲料+苗种/能耗/人工摊销" />
        <MetricCard label="完全单位成本" value={stats?.avgUnitCostFullyLoadedYuanPerKg != null ? `¥${stats.avgUnitCostFullyLoadedYuanPerKg}/kg` : '—'} tone="brand" hint="元/kg 产出鱼（完全）" />
      </div>

      {stats?.pricingConfigured === false || (stats?.unpricedFeedKg ?? 0) > 0 ? (
        <div className="mb-6 rounded-lg border border-warn-border bg-warn-bg px-4 py-3 text-[13px] text-warn-fg">
          {stats?.pricingConfigured === false
            ? '尚未配置饲料单价，成本暂不计算（不使用任何默认价）。管理员请在下方「成本参数」录入按生效日的饲料价表。'
            : `有 ${stats?.unpricedFeedKg}kg 投喂缺少覆盖单价，其饲料/完全成本暂按未定价处理，请补录对应生效日的单价。`}
        </div>
      ) : null}


      <SectionTitle title="存塘批次" />
      <Card className="mb-6 p-0">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-hairline bg-slate-50/60">
              {['批次', '单元', '品种', '尾数', '均重kg', '生物量kg', '日龄', '存活', 'FCE', '投喂kg', '损耗', '捕捞kg', '状态', '操作'].map((h) => (
                <th key={h} scope="col" className="th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(batches ?? []).map((b) => (<BatchRow key={b.id} b={b} others={(batches ?? []).filter((x) => x.id !== b.id)} canRecord={canRecord} />))}
            {(batches ?? []).length === 0 && (
              <tr><td colSpan={14} className="td"><EmptyHint>暂无批次</EmptyHint></td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {canManage ? (
        <Card className="p-6">
          <SectionTitle title="新建批次（放苗）" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <label className="text-[13px] text-slate-500">单元<input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" /></label>
            <label className="text-[13px] text-slate-500">品种<input value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" /></label>
            <label className="text-[13px] text-slate-500">尾数<input type="number" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" /></label>
            <label className="text-[13px] text-slate-500">均重kg<input type="number" step="0.01" value={form.avgWeightKg} onChange={(e) => setForm({ ...form, avgWeightKg: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" /></label>
            <label className="text-[13px] text-slate-500">放苗日期<input type="date" value={form.stockedAt} onChange={(e) => setForm({ ...form, stockedAt: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" /></label>
          </div>
          <button onClick={submit} disabled={create.isPending} className="btn btn-primary mt-4 disabled:opacity-60">{create.isPending ? '创建中…' : '创建批次'}</button>
        </Card>
      ) : null}

      <CostPanel isAdmin={role === 'admin'} />
    </div>
  );
}
